// View-angle math shared by the input layer and the camera. The quantizers reproduce
// `gr_core::math::{quantize_yaw, quantize_pitch}` exactly (f32 arithmetic via Math.fround) so the
// u16 values the client sends are bit-identical to what the Rust side would produce.

export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;

const f = Math.fround;
const F32_PI = f(Math.PI);
const F32_TAU = f(2 * Math.PI);
const F32_FRAC_PI_2 = f(Math.PI / 2);

/** Maximum look-up / look-down angle in radians (+-89 degrees). */
export const PITCH_LIMIT = 89 * DEG2RAD;

/** Yaw in radians -> u16 (full circle / 65536), like `gr_core::math::quantize_yaw`. */
export function quantizeYaw(yaw: number): number {
  const y = f(yaw);
  const t = f(y - f(F32_TAU * Math.floor(f(y / F32_TAU)))); // [0, TAU)
  return Math.round(f(f(t / F32_TAU) * 65536)) & 0xffff;
}

/** Inverse of `quantizeYaw`; returns [0, TAU). */
export function dequantizeYaw(q: number): number {
  return f(q * f(F32_TAU / 65536));
}

/** Pitch in radians (clamped to +-PI/2) -> u16 (65535 steps), like `gr_core::math::quantize_pitch`. */
export function quantizePitch(pitch: number): number {
  const p = Math.min(F32_FRAC_PI_2, Math.max(-F32_FRAC_PI_2, f(pitch)));
  return Math.round(f(f(f(p + F32_FRAC_PI_2) / F32_PI) * 65535));
}

/** Inverse of `quantizePitch`. */
export function dequantizePitch(q: number): number {
  return f(f(f(q / 65535) * F32_PI) - F32_FRAC_PI_2);
}

/** Wraps an angle to (-PI, PI]. */
export function wrapAngle(a: number): number {
  let r = a - 2 * Math.PI * Math.floor((a + Math.PI) / (2 * Math.PI));
  if (r <= -Math.PI) r += 2 * Math.PI;
  return r;
}

/** Wraps a yaw to [0, TAU). */
export function wrapYaw(yaw: number): number {
  const tau = 2 * Math.PI;
  const t = yaw - tau * Math.floor(yaw / tau);
  return t >= tau ? 0 : t;
}

export function clampPitch(pitch: number): number {
  return Math.min(PITCH_LIMIT, Math.max(-PITCH_LIMIT, pitch));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

export function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
